import React from 'react';

// Top-level boundary so a render crash anywhere in the tree shows the
// real error and stack inline instead of a blank white screen. The
// "settings page goes white for super-admin" bug went unsolved for too
// many rounds because React swallowed the error without a boundary —
// this fixes the visibility problem first, then we fix the underlying
// crash with the stack in hand.
//
// Plain class component because hooks don't have a getDerivedStateFromError
// equivalent. Keeps zero external deps on purpose.
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface in console with a recognizable prefix so it's easy to
    // grep across the user's DevTools paste.
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, info?.componentStack);
    this.setState({ info });
  }

  render() {
    if (!this.state.error) return this.props.children;
    const stack = (this.state.info && this.state.info.componentStack) || '';
    return (
      <div style={{ padding: 24, fontFamily: 'Menlo, Monaco, monospace', fontSize: 13, color: '#111', background: '#fff' }}>
        <h3 style={{ color: '#b91c1c', fontFamily: 'system-ui, sans-serif' }}>
          The page crashed
        </h3>
        <p style={{ fontFamily: 'system-ui, sans-serif', fontSize: 14, color: '#374151' }}>
          This box is the React error boundary. Send a screenshot of
          everything below to support and we'll fix it.
        </p>
        <pre style={{ background: '#fef2f2', padding: 12, borderRadius: 4, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {String(this.state.error?.message || this.state.error || 'Unknown error')}
        </pre>
        {this.state.error?.stack && (
          <details style={{ marginTop: 12 }}>
            <summary style={{ cursor: 'pointer', fontFamily: 'system-ui, sans-serif', fontSize: 13 }}>Error stack</summary>
            <pre style={{ background: '#f3f4f6', padding: 12, borderRadius: 4, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
              {String(this.state.error.stack)}
            </pre>
          </details>
        )}
        {stack && (
          <details style={{ marginTop: 12 }} open>
            <summary style={{ cursor: 'pointer', fontFamily: 'system-ui, sans-serif', fontSize: 13 }}>Component stack (top is most specific)</summary>
            <pre style={{ background: '#f3f4f6', padding: 12, borderRadius: 4, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
              {String(stack)}
            </pre>
          </details>
        )}
        <button
          onClick={() => this.setState({ error: null, info: null })}
          style={{ marginTop: 16, padding: '8px 14px', fontSize: 13, background: '#15172b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
        >
          Try again
        </button>
      </div>
    );
  }
}
